import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Camera, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';

const LoginPage = () => {
  const { t } = useLanguage();
  const { login, error } = useAuth();
  const navigate = useNavigate();
  
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [localError, setLocalError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLocalError('');
    
    if (!username || !password) {
      setLocalError(t.login?.errorRequired || 'Inserisci username e password');
      return;
    }
    
    setIsLoading(true);
    
    const result = await login(username, password);
    
    setIsLoading(false);
    
    if (result.success) {
      // Redirect based on role
      if (result.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/gallery');
      }
    } else {
      setLocalError(result.error);
    }
  };

  return (
    <div className="min-h-screen bg-sky-100 flex flex-col">
      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-md">
          {/* Login Card */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-[#2a2a1c] rounded-full flex items-center justify-center">
                <Camera className="w-8 h-8 text-white" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-2xl font-serif text-center mb-2">
              {t.login?.title || 'Accedi alla Galleria'}
            </h1>
            <p className="text-gray-500 text-center text-sm mb-8">
              {t.login?.subtitle || 'Inserisci le credenziali fornite dal fotografo'}
            </p>

            {/* Error Message */}
            {(localError || error) && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {localError || error}
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Username */}
              <div>
                <label htmlFor="username" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.login?.username || 'Username'}
                </label>
                <input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition-colors"
                  placeholder={t.login?.usernamePlaceholder || 'Inserisci username'}
                  autoComplete="username"
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  {t.login?.password || 'Password'}
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-black transition-colors pr-12"
                    placeholder={t.login?.passwordPlaceholder || 'Inserisci password'}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-black text-white font-medium rounded-lg hover:bg-gray-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    {t.login?.loading || 'Accesso in corso...'}
                  </>
                ) : (
                  t.login?.submit || 'Accedi'
                )}
              </button>
            </form>
          </div>

          {/* Back to Home */}
          <p className="text-center mt-6 text-sm text-gray-500">
            <a href="/" className="text-black hover:underline">
              {t.login?.backHome || '← Torna alla home'}
            </a>
          </p>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-6 text-center text-sm text-gray-400">
        © 2025 Pic-Time
      </footer>
    </div>
  );
};

export default LoginPage;
